import { z } from 'zod';
import { themePreferenceSchema } from './theme.js';

/**
 * Roles are coarse; fine-grained access comes from the customer scope on the
 * user. A `customer` user only ever sees resources/tickets/invoices belonging to
 * their own customerId — enforced in the data layer, not the UI.
 */
export const ROLES = ['owner', 'admin', 'engineer', 'technician', 'billing', 'viewer', 'customer', 'customer_admin'] as const;
export type Role = (typeof ROLES)[number];

/**
 * The two "client access" tiers — `customer` (viewer) and `customer_admin` —
 * both carry a `customerId` and are locked to that one tenant everywhere the
 * data layer checks it. Kept as its own list (rather than inlining the
 * literal check at each call site) so a third client tier, if one is ever
 * added, only needs updating here.
 */
export const CUSTOMER_ROLES = ['customer', 'customer_admin'] as const;
export function isCustomerRole(role: string | undefined | null): boolean {
  return role != null && (CUSTOMER_ROLES as readonly string[]).includes(role);
}

export const PERMISSIONS = [
  'resource:read',
  'resource:write',
  'metric:read',
  'alert:read',
  'alert:ack',
  'alert:write',
  'ticket:read',
  'ticket:write',
  'ticket:assign',
  'task:read',
  'task:write',
  'billing:read',
  'billing:write',
  'plugin:read',
  'plugin:manage',
  'user:read',
  'user:manage',
  'theme:read',
  'theme:manage',
  'settings:manage',
  'audit:read',
  // Password safe. `vault:read` only lists metadata; seeing an actual secret
  // needs `vault:reveal`, which is a separate, audited grant.
  'vault:read',
  'vault:reveal',
  'vault:write',
  'vault:manage',
  'backup:read',
  'backup:write',
  'backup:run',
  // Overwrites a live database or replays saved config onto a live device —
  // meaningfully more consequential than starting a backup, so it is its own
  // grant rather than riding on backup:run.
  'backup:restore',
  // Diagnostics. Split three ways because the blast radius differs sharply:
  // a ping is harmless, a subnet sweep is noisy and dual-use, and proxying into
  // a device's admin UI is effectively remote hands on that device.
  'tools:run',
  'tools:scan',
  'tools:proxy',
  // Logs into the device and writes config — enabling a service, adding a
  // firewall rule. Strictly more consequential than tools:proxy, so it gets
  // the same "owner/admin only by default" treatment.
  'tools:provision',
  // Reach a client's devices only while assigned to one of that client's open
  // tickets, with the server filling in the stored login — the holder never
  // sees a device password. The ticket assignment is the grant; closing or
  // reassigning the ticket ends it.
  'access:ticket',
  // Deliberately its own grant rather than riding on billing:write, which
  // also unlocks the accounts ledger and the invoice/subscription catalog —
  // none of which a client's own admin should ever touch.
  'utility:write',
  // Invite/edit/remove users within your OWN client account only — not the
  // staff-facing user:manage, whose routes assume a global staff actor and
  // aren't tenant-scoped.
  'client_user:manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  owner: PERMISSIONS,
  // admin matches owner today; kept separate so ownership-only actions
  // (transferring the instance, deleting the last owner) can diverge later.
  admin: PERMISSIONS,
  engineer: [
    'resource:read',
    'resource:write',
    'metric:read',
    'alert:read',
    'alert:ack',
    'alert:write',
    'ticket:read',
    'ticket:write',
    'ticket:assign',
    'task:read',
    'task:write',
    'plugin:read',
    'theme:read',
    'vault:read',
    'vault:reveal',
    'vault:write',
    'backup:read',
    'backup:run',
    'tools:run',
    'tools:scan',
    // `tools:proxy` is deliberately withheld from engineers by default —
    // reaching a firewall's admin page is an ownership-level grant, not a
    // troubleshooting one. Promote per-user where that is the real workflow.
  ],
  // Works assigned tickets and reaches that client's devices through
  // server-filled logins (`access:ticket`). No vault:reveal and no tools:proxy:
  // a technician fixes devices without ever holding their passwords.
  technician: [
    'resource:read',
    'metric:read',
    'alert:read',
    'alert:ack',
    'ticket:read',
    'ticket:write',
    'task:read',
    'task:write',
    'plugin:read',
    'theme:read',
    'backup:read',
    'tools:run',
    'access:ticket',
  ],
  billing: ['resource:read', 'metric:read', 'billing:read', 'billing:write', 'utility:write', 'ticket:read', 'theme:read'],
  // A viewer can see that a credential exists but never its value — that split
  // is the whole point of separating vault:read from vault:reveal.
  viewer: ['resource:read', 'metric:read', 'alert:read', 'ticket:read', 'task:read', 'plugin:read', 'theme:read', 'vault:read', 'backup:read'],
  customer: ['resource:read', 'metric:read', 'alert:read', 'ticket:read', 'ticket:write', 'billing:read', 'theme:read', 'backup:read'],
  /**
   * "System-admin parity, scoped to this one client." Includes every
   * tools: permission — every `/resource/:id/...` route (proxy, AMI
   * provisioning, tunnels, per-resource diagnostics) resolves the resource
   * through `resourceForUser`, which already 404s on a cross-tenant id, so
   * these are tenant-safe by construction, not by convention. The one class
   * of tools: route that ISN'T resource-scoped — free-target ping/
   * traceroute/port-scan/sweep/DNS/HTTP/LLDP/neighbours, where the caller
   * types an arbitrary address rather than picking a registered resource —
   * has no `customerId` to check, so those routes reject a client-access
   * role outright at the handler (see `assertNotFreeTargetClient` in
   * tools-routes.ts) even though the permission bit is held.
   *
   * Still deliberately excludes: ticket:assign (staff triage concept),
   * billing:write (the provider-side ledger and invoice/subscription/plan
   * catalog), plugin:read, plugin:manage, settings:manage, theme:manage and
   * audit:read (instance-wide, not "this client's resources"), and every
   * backup: permission (the backups plugin's tenant-isolation hasn't been
   * audited the way tools-routes.ts has). user:manage is likewise withheld
   * in favor of the narrower client_user:manage, since user:manage's routes
   * assume a global staff actor with no tenant check.
   */
  customer_admin: [
    'resource:read',
    'resource:write',
    'metric:read',
    'alert:read',
    'alert:ack',
    'alert:write',
    'ticket:read',
    'ticket:write',
    'task:read',
    'task:write',
    'billing:read',
    'vault:read',
    'vault:reveal',
    'vault:write',
    'vault:manage',
    'theme:read',
    'utility:write',
    'client_user:manage',
    'tools:run',
    'tools:scan',
    'tools:proxy',
    'tools:provision',
  ],
};

export function can(role: Role, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/**
 * Which sites/resources page this user with an audible push alert on a
 * health change. Independent of AlertRule/NotificationPolicy — a user's own
 * "what I personally want to hear about", not a routing rule.
 */
export const alertWatchSchema = z.object({
  siteIds: z.array(z.string()).default([]),
  resourceIds: z.array(z.string()).default([]),
});
export type AlertWatch = z.infer<typeof alertWatchSchema>;

export const userSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string().min(1),
  role: z.enum(ROLES),
  /** Non-null for client-access role users (`customer`/`customer_admin`); scopes every query they make. */
  customerId: z.string().nullable().default(null),
  phone: z.string().nullable().default(null),
  /** Receive SMS/email pages for alerts routed to them. */
  notifyEmail: z.boolean().default(true),
  notifySms: z.boolean().default(false),
  notifyPush: z.boolean().default(true),
  themePreference: themePreferenceSchema.default({
    themeId: null,
    mode: 'system',
    highContrast: null,
    reduceMotion: null,
    density: null,
    fontScale: null,
  }),
  timezone: z.string().default('UTC'),
  locale: z.string().default('en'),
  alertWatch: alertWatchSchema.default({ siteIds: [], resourceIds: [] }),
  /** Authenticator-app 2FA turned on for this account. Never carries the secret itself. */
  totpEnabled: z.boolean().default(false),
  /** Email-code 2FA turned on for this account — an alternative second factor to the authenticator app. */
  emailOtpEnabled: z.boolean().default(false),
  active: z.boolean().default(true),
  lastLoginAt: z.number().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type User = z.infer<typeof userSchema>;

export const OTP_METHODS = ['totp', 'email'] as const;
export type OtpMethod = (typeof OTP_METHODS)[number];

export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  /** A code from the chosen second factor, once the account has one enabled. */
  otp: z.string().optional(),
  /** Which second factor `otp` answers — defaults to the account's preferred method when omitted. */
  otpMethod: z.enum(OTP_METHODS).optional(),
});
export type LoginInput = z.infer<typeof loginSchema>;

/** What `/api/auth/login` sends back instead of tokens when a second factor is still needed. */
export interface OtpChallenge {
  otpRequired: true;
  methods: OtpMethod[];
  method: OtpMethod;
  /** Set once an email code has actually been sent for this attempt, e.g. "j***@example.com". */
  sentTo?: string;
}

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface SessionUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  customerId: string | null;
  permissions: Permission[];
  /** Set while an administrator is connected as this user to test the role; who they really are. */
  impersonatedBy?: { id: string; email: string; name: string };
}

/** How long a "connect as" session lasts; it has no refresh token, so it simply ends. */
export const CONNECT_AS_TTL = '2h';

export const customerSchema = z.object({
  id: z.string(),
  name: z.string().min(1),
  code: z.string().min(1),
  contactEmail: z.string().nullable().default(null),
  contactPhone: z.string().nullable().default(null),
  address: z.string().nullable().default(null),
  /** Bound white-label theme; overrides the global default for their users. */
  themeId: z.string().nullable().default(null),
  active: z.boolean().default(true),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Customer = z.infer<typeof customerSchema>;

/**
 * A physical location belonging to a customer. Resources attach to a site, so
 * the estate reads customer -> site -> resource.
 */
export const siteSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  name: z.string().min(1),
  /** Short label unique within the customer, e.g. "HQ", "BR-02". */
  code: z.string().nullable().default(null),
  address: z.string().nullable().default(null),
  contactName: z.string().nullable().default(null),
  contactPhone: z.string().nullable().default(null),
  /** IANA zone, used when rendering that site's maintenance windows. */
  timezone: z.string().nullable().default(null),
  notes: z.string().nullable().default(null),
  active: z.boolean().default(true),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Site = z.infer<typeof siteSchema>;

export const siteCreateSchema = siteSchema
  .omit({ id: true, createdAt: true, updatedAt: true })
  .partial({ code: true, address: true, contactName: true, contactPhone: true, timezone: true, notes: true, active: true });
export type SiteCreate = z.infer<typeof siteCreateSchema>;

/** The owning customer is fixed at creation; moving a site between tenants
 *  would orphan its resources' scope, so `customerId` is not patchable. */
export const siteUpdateSchema = siteCreateSchema.omit({ customerId: true }).partial();
export type SiteUpdate = z.infer<typeof siteUpdateSchema>;
